import { useEffect, useMemo, useRef, useState } from 'react';
import { apiRequest } from '@/lib/api';

type Lieu = {
  id: number;
  nom: string;
  ville?: string | null;
  quartier?: string | null;
  capacite?: number | null;
  prix_heure?: number | null;
  caution?: number | null;
  image?: string | null;
  description?: string | null;
  conditions?: string | null;
  telephone?: string | null;
  email?: string | null;
  equipements?: string[] | null;
  mois_disponibilites?: number | null;
  annee_disponibilites?: number | null;
  disponibilites?: number[] | null;
  dates_reservees?: number[] | null;
  creneaux?: Array<{ id: string; label: string; price?: number }> | null;
  visite_autorisee?: boolean | null;
  heures_visite?: string[] | null;
  statut?: 'disponible' | 'indisponible' | string;
};

type ReservationLieu = {
  id: number;
  lieu_location_id: number;
  lieu_nom?: string | null;
  lieu_ville?: string | null;
  nom: string;
  email: string;
  telephone?: string | null;
  adresse?: string | null;
  date_reservation: string;
  creneau_id?: string | null;
  creneau_label: string;
  montant: number;
  message?: string | null;
  statut: 'paiement_en_attente' | 'envoyee' | 'confirmee' | 'refusee' | 'annulee' | string;
  status_paiement?: string | null;
  payment_method?: string | null;
  checkout_url?: string | null;
  transaction_id?: string | null;
  created_at?: string;
};

type LieuForm = {
  nom: string;
  ville: string;
  quartier: string;
  capacite: string;
  prix_heure: string;
  caution: string;
  image: string;
  description: string;
  conditions: string;
  telephone: string;
  email: string;
  equipements: string;
  mois_disponibilites: string;
  annee_disponibilites: string;
  disponibilites: string;
  dates_reservees: string;
  statut: 'disponible' | 'indisponible' | 'deleted';
  creneaux: string;
  visite_autorisee: boolean;
  heures_visite: string;
};

const emptyForm: LieuForm = {
  nom: '',
  ville: '',
  quartier: '',
  capacite: '',
  prix_heure: '',
  caution: '',
  image: '',
  description: '',
  conditions: '',
  telephone: '',
  email: '',
  equipements: '',
  mois_disponibilites: String(new Date().getMonth() + 1),
  annee_disponibilites: String(new Date().getFullYear()),
  disponibilites: '',
  dates_reservees: '',
  statut: 'disponible',
  creneaux: '',
  visite_autorisee: true,
  heures_visite: '08h00, 09h00, 10h00, 11h00, 14h00, 15h00, 16h00, 17h00',
};

const equipmentSuggestions = [
  'Climatisee',
  'Sono/Micro',
  'Parking',
  'Traiteur',
  'Projecteur',
  'Wifi',
  'Chaises',
  'Tables',
];

const previewFallbackImage = 'https://readdy.ai/api/search-image?query=elegant%20event%20hall%20interior%20clean%20modern%20venue%20soft%20light%20premium&width=800&height=500&seq=lieu-admin-preview&orientation=landscape';
const cardFallbackImage = 'https://readdy.ai/api/search-image?query=hall%20room%20event%20venue%20clean%20interior&width=800&height=500&seq=lieu-fallback&orientation=landscape';
const monthLabels = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
const RESERVATIONS_PAGE_SIZE = 8;

const parseCommaList = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const parseCreneaux = (value: string) =>
  value
    .split('\n')
    .map((line, index) => {
      const [labelPart, pricePart] = line.split('|').map((item) => item?.trim());
      if (!labelPart) return null;

      return {
        id: `creneau-${index + 1}`,
        label: labelPart,
        price: pricePart ? Number(pricePart) : 0,
      };
    })
    .filter(Boolean) as Array<{ id: string; label: string; price?: number }>;

const parseHeuresVisite = (value: string) => parseCommaList(value);

const parseDayList = (value: string) =>
  value
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isInteger(item) && item >= 1 && item <= 31)
    .sort((left, right) => left - right);

const buildCalendar = (year: number, month: number) => {
  const firstDay = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstColumn = (firstDay.getDay() + 6) % 7;
  const cells: Array<number | null> = [];

  for (let index = 0; index < firstColumn; index += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);

  return cells;
};

const buildMonthDayRange = (year: number, month: number, startingDay = 1) => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDay = Math.max(1, Math.min(startingDay, daysInMonth));

  return Array.from({ length: Math.max(0, daysInMonth - firstDay + 1) }, (_, index) => firstDay + index);
};

const toForm = (lieu: Lieu): LieuForm => ({
  nom: lieu.nom ?? '',
  ville: lieu.ville ?? '',
  quartier: lieu.quartier ?? '',
  capacite: lieu.capacite ? String(lieu.capacite) : '',
  prix_heure: lieu.prix_heure ? String(lieu.prix_heure) : '',
  caution: lieu.caution ? String(lieu.caution) : '',
  image: lieu.image ?? '',
  description: lieu.description ?? '',
  conditions: lieu.conditions ?? '',
  telephone: lieu.telephone ?? '',
  email: lieu.email ?? '',
  equipements: (lieu.equipements ?? []).join(', '),
  mois_disponibilites: lieu.mois_disponibilites ? String(lieu.mois_disponibilites) : String(new Date().getMonth() + 1),
  annee_disponibilites: lieu.annee_disponibilites ? String(lieu.annee_disponibilites) : String(new Date().getFullYear()),
  disponibilites: (lieu.disponibilites ?? []).join(', '),
  dates_reservees: (lieu.dates_reservees ?? []).join(', '),
  statut: (lieu.statut as 'disponible' | 'indisponible') ?? 'disponible',
  creneaux: (lieu.creneaux ?? []).map((creneau) => `${creneau.label}${creneau.price ? ` | ${creneau.price}` : ''}`).join('\n'),
  visite_autorisee: lieu.visite_autorisee ?? true,
  heures_visite: (lieu.heures_visite ?? []).join(', '),
});

const formatMoney = (value?: number | null) =>
  `${Number(value ?? 0).toLocaleString('fr-FR')} F`;

const reservationStatusLabels: Record<string, string> = {
  paiement_en_attente: 'Paiement non validé',
  envoyee: 'Envoyée',
  confirmee: 'Confirmée',
  refusee: 'Refusée',
  annulee: 'Annulée',
};

export default function LieuxManagement() {
  const [lieux, setLieux] = useState<Lieu[]>([]);
  const [reservations, setReservations] = useState<ReservationLieu[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formFeedback, setFormFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [form, setForm] = useState<LieuForm>(emptyForm);
  const [editingLieuId, setEditingLieuId] = useState<number | null>(null);
  const [selectedLieu, setSelectedLieu] = useState<Lieu | null>(null);
  const [selectedReservation, setSelectedReservation] = useState<ReservationLieu | null>(null);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ reservation: ReservationLieu; statut: ReservationLieu['statut'] } | null>(null);
  const [pendingReservationDelete, setPendingReservationDelete] = useState<ReservationLieu | null>(null);
  const [search, setSearch] = useState('');
  const [lieuStatusFilter, setLieuStatusFilter] = useState<'all' | 'disponible' | 'indisponible' | 'deleted'>('all');
  const [reservationStatus, setReservationStatus] = useState<'all' | 'paiement_en_attente' | 'envoyee' | 'confirmee' | 'refusee' | 'annulee'>('all');
  const [reservationKind, setReservationKind] = useState<'reservations' | 'visites'>('reservations');
  const [reservationPage, setReservationPage] = useState(1);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [calendarMode, setCalendarMode] = useState<'available' | 'reserved'>('available');
  const imageInputRef = useRef<HTMLInputElement>(null);
  const formSectionRef = useRef<HTMLDivElement>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [lieuxResult, reservationsResult] = await Promise.allSettled([
        apiRequest<Lieu[]>('/admin/lieux-locations?include_deleted=1'),
        apiRequest<ReservationLieu[]>('/admin/lieux-locations/reservations'),
      ]);

      if (lieuxResult.status === 'fulfilled') {
        setLieux(lieuxResult.value);
      } else {
        console.error('Lieux load error:', lieuxResult.reason);
        setLieux([]);
      }

      if (reservationsResult.status === 'fulfilled') {
        setReservations(reservationsResult.value);
      } else {
        console.error('Reservations load error:', reservationsResult.reason);
        setReservations([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingLieuId(null);
    setImageFile(null);
    setFormFeedback(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const payload = useMemo(() => ({
    nom: form.nom,
    ville: form.ville || undefined,
    quartier: form.quartier || undefined,
    capacite: form.capacite ? Number(form.capacite) : 0,
    prix_heure: form.prix_heure ? Number(form.prix_heure) : 0,
    caution: form.caution ? Number(form.caution) : undefined,
    image: form.image || undefined,
    description: form.description || undefined,
    conditions: form.conditions || undefined,
    telephone: form.telephone || undefined,
    email: form.email || undefined,
    equipements: parseCommaList(form.equipements),
    mois_disponibilites: form.mois_disponibilites ? Number(form.mois_disponibilites) : undefined,
    annee_disponibilites: form.annee_disponibilites ? Number(form.annee_disponibilites) : undefined,
    disponibilites: parseDayList(form.disponibilites),
    dates_reservees: parseDayList(form.dates_reservees),
    statut: form.statut,
    creneaux: parseCreneaux(form.creneaux),
    visite_autorisee: form.visite_autorisee,
    heures_visite: parseHeuresVisite(form.heures_visite),
  }), [form]);

  const selectedEquipements = useMemo(() => parseCommaList(form.equipements), [form.equipements]);
  const selectedCreneaux = useMemo(() => parseCreneaux(form.creneaux), [form.creneaux]);
  const selectedDisponibilites = useMemo(() => parseDayList(form.disponibilites), [form.disponibilites]);
  const selectedReservedDates = useMemo(() => parseDayList(form.dates_reservees), [form.dates_reservees]);
  const calendarMonth = Number(form.mois_disponibilites || new Date().getMonth() + 1);
  const calendarYear = Number(form.annee_disponibilites || new Date().getFullYear());
  const calendarDays = useMemo(() => buildCalendar(calendarYear, calendarMonth), [calendarYear, calendarMonth]);

  const handleSubmit = async () => {
    if (!form.nom.trim()) {
      setFormFeedback({
        type: 'error',
        message: 'Le nom de la salle est obligatoire.',
      });
      return;
    }

    setSubmitting(true);
    setFormFeedback(null);

    try {
      const body = (() => {
        if (!imageFile) {
          return JSON.stringify(payload);
        }

        const formData = new FormData();
        formData.append('nom', form.nom);

        if (form.ville) formData.append('ville', form.ville);
        if (form.quartier) formData.append('quartier', form.quartier);
        if (form.capacite) formData.append('capacite', form.capacite);
        if (form.prix_heure) formData.append('prix_heure', form.prix_heure);
        if (form.caution) formData.append('caution', form.caution);
        if (form.image && !form.image.startsWith('blob:')) formData.append('image', form.image);
        if (form.description) formData.append('description', form.description);
        if (form.conditions) formData.append('conditions', form.conditions);
        if (form.telephone) formData.append('telephone', form.telephone);
        if (form.email) formData.append('email', form.email);
        if (form.mois_disponibilites) formData.append('mois_disponibilites', form.mois_disponibilites);
        if (form.annee_disponibilites) formData.append('annee_disponibilites', form.annee_disponibilites);
        formData.append('statut', form.statut);
        formData.append('visite_autorisee', form.visite_autorisee ? '1' : '0');
        formData.append('image_file', imageFile);

        parseCommaList(form.equipements).forEach((equipement, index) => {
          formData.append(`equipements[${index}]`, equipement);
        });

        parseDayList(form.disponibilites).forEach((day, index) => {
          formData.append(`disponibilites[${index}]`, String(day));
        });

        parseDayList(form.dates_reservees).forEach((day, index) => {
          formData.append(`dates_reservees[${index}]`, String(day));
        });

        parseCreneaux(form.creneaux).forEach((creneau, index) => {
          formData.append(`creneaux[${index}][id]`, creneau.id);
          formData.append(`creneaux[${index}][label]`, creneau.label);
          formData.append(`creneaux[${index}][price]`, String(creneau.price ?? 0));
        });

        parseHeuresVisite(form.heures_visite).forEach((heure, index) => {
          formData.append(`heures_visite[${index}]`, heure);
        });

        return formData;
      })();

      if (editingLieuId) {
        if (body instanceof FormData) {
          body.append('_method', 'PUT');
        }

        await apiRequest(`/lieux-locations/${editingLieuId}`, {
          method: body instanceof FormData ? 'POST' : 'PUT',
          body,
        });
      } else {
        await apiRequest('/lieux-locations', {
          method: 'POST',
          body,
        });
      }

      resetForm();
      setFormFeedback({
        type: 'success',
        message: editingLieuId ? 'Salle mise à jour avec succès.' : 'Salle ajoutée avec succès.',
      });
      await loadData();
    } catch (error) {
      setFormFeedback({
        type: 'error',
        message: error instanceof Error ? error.message : 'Impossible d’enregistrer cette salle pour le moment.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (lieu: Lieu) => {
    setSelectedLieu(null);
    setEditingLieuId(lieu.id);
    setForm(toForm(lieu));
    setImageFile(null);
    setFormFeedback(null);
    formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleImageFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setImageFile(file);
    setForm((current) => ({
      ...current,
      image: URL.createObjectURL(file),
    }));
  };

  const handleDelete = async (lieuId: number) => {
    const confirmed = window.confirm('Mettre cette salle dans la corbeille ?');
    if (!confirmed) {
      return;
    }

    await apiRequest(`/lieux-locations/${lieuId}`, {
      method: 'DELETE',
    });

    if (editingLieuId === lieuId) {
      resetForm();
    }

    await loadData();
  };

  const handleRestore = async (lieuId: number) => {
    const confirmed = window.confirm('Restaurer cette salle ?');
    if (!confirmed) {
      return;
    }

    await apiRequest(`/lieux-locations/${lieuId}/restore`, {
      method: 'POST',
    });

    await loadData();
  };

  const handleReservationStatus = async (reservationId: number, statut: ReservationLieu['statut']) => {
    await apiRequest(`/admin/reservations-lieux/${reservationId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ statut }),
    });
    await loadData();
  };

  const requestReservationStatusChange = (reservation: ReservationLieu, statut: ReservationLieu['statut']) => {
    if (reservation.statut === statut) return;
    setPendingStatusChange({ reservation, statut });
  };

  const confirmReservationStatusChange = async () => {
    if (!pendingStatusChange) return;
    const { reservation, statut } = pendingStatusChange;
    setPendingStatusChange(null);
    await handleReservationStatus(reservation.id, statut);
  };

  const confirmReservationDelete = async () => {
    if (!pendingReservationDelete) return;
    const reservationId = pendingReservationDelete.id;
    setPendingReservationDelete(null);
    if (selectedReservation?.id === reservationId) {
      setSelectedReservation(null);
    }
    await apiRequest(`/admin/reservations-lieux/${reservationId}`, {
      method: 'DELETE',
    });
    await loadData();
  };

  const toggleEquipement = (label: string) => {
    const next = selectedEquipements.includes(label)
      ? selectedEquipements.filter((item) => item !== label)
      : [...selectedEquipements, label];

    setForm((current) => ({
      ...current,
      equipements: next.join(', '),
    }));
  };

  const toggleCalendarDay = (day: number) => {
    const source = calendarMode === 'available' ? selectedDisponibilites : selectedReservedDates;
    const other = calendarMode === 'available' ? selectedReservedDates : selectedDisponibilites;
    const nextSource = source.includes(day)
      ? source.filter((item) => item !== day)
      : [...source, day].sort((left, right) => left - right);
    const nextOther = other.filter((item) => item !== day);

    setForm((current) => ({
      ...current,
      disponibilites: (calendarMode === 'available' ? nextSource : nextOther).join(', '),
      dates_reservees: (calendarMode === 'reserved' ? nextSource : nextOther).join(', '),
    }));
  };

  const setAvailableRangeFromDate = (offsetDays: number) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const threshold = new Date(today);
    threshold.setDate(today.getDate() + offsetDays);

    const targetMonth = threshold.getMonth() + 1;
    const targetYear = threshold.getFullYear();
    const nextAvailableDays = buildMonthDayRange(targetYear, targetMonth, threshold.getDate());

    setForm((current) => ({
      ...current,
      mois_disponibilites: String(targetMonth),
      annee_disponibilites: String(targetYear),
      disponibilites: nextAvailableDays.join(', '),
      dates_reservees: (calendarMode === 'available'
        ? selectedReservedDates.filter((day) => !nextAvailableDays.includes(day))
        : selectedReservedDates
      ).join(', '),
    }));
  };

  const clearCalendarSelection = () => {
    setForm((current) => ({
      ...current,
      disponibilites: '',
      dates_reservees: '',
    }));
  };

  const filteredLieux = lieux.filter((lieu) => {
    const q = search.toLowerCase();
    const status = lieu.statut === 'deleted' ? 'deleted' : lieu.statut === 'indisponible' ? 'indisponible' : 'disponible';
    const matchesStatus = lieuStatusFilter === 'all' || status === lieuStatusFilter;
    const matchesSearch = !q || `${lieu.nom} ${lieu.ville ?? ''} ${lieu.quartier ?? ''}`.toLowerCase().includes(q);
    return matchesStatus && matchesSearch;
  });

  const filteredReservations = reservations.filter((reservation) => {
    const isVisit = reservation.creneau_id === 'visite_gratuite';
    const matchesKind = reservationKind === 'visites' ? isVisit : !isVisit;
    const matchesStatus = reservationStatus === 'all' || reservation.statut === reservationStatus;
    return matchesKind && matchesStatus;
  });

  const reservationLastPage = Math.max(1, Math.ceil(filteredReservations.length / RESERVATIONS_PAGE_SIZE));
  const paginatedReservations = filteredReservations.slice(
    (reservationPage - 1) * RESERVATIONS_PAGE_SIZE,
    reservationPage * RESERVATIONS_PAGE_SIZE
  );

  useEffect(() => {
    setReservationPage(1);
  }, [reservationKind, reservationStatus]);

  useEffect(() => {
    if (reservationPage > reservationLastPage) {
      setReservationPage(reservationLastPage);
    }
  }, [reservationLastPage, reservationPage]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-[540px_minmax(0,1fr)] gap-6">
        <div ref={formSectionRef} className="relative bg-gradient-to-br from-[#fffaf0] via-white to-[#f6f8ff] rounded-[28px] border-2 border-[#f3c13a] p-6 space-y-5 shadow-[0_24px_60px_rgba(243,193,58,0.16)]">
          <div className="absolute -top-3 left-6 rounded-full bg-[#f3c13a] px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.18em] text-white shadow-[0_10px_24px_rgba(243,193,58,0.28)]">
            Formulaire d'ajout
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-navy">{editingLieuId ? 'Modifier une salle' : 'Ajouter une salle'}</h3>
              <p className="text-xs text-gray-400">Crée une salle qui ressemble vraiment au rendu mobile : image, capacité, équipements et créneaux.</p>
            </div>
            {editingLieuId ? (
              <button onClick={resetForm} className="text-xs text-gray-500 hover:text-navy cursor-pointer whitespace-nowrap">
                Annuler
              </button>
            ) : null}
          </div>

          {formFeedback ? (
            <div className={`rounded-2xl px-4 py-3 text-sm font-medium ${
              formFeedback.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {formFeedback.message}
            </div>
          ) : null}

          <div className="overflow-hidden rounded-[26px] border border-[#e4e8f3] bg-[#f7f8fc] ring-1 ring-[#f7df97]">
            <div className="relative h-56 bg-navy">
              <img
                src={form.image || previewFallbackImage}
                alt={form.nom || 'Aperçu de la salle'}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#10172bcc] via-[#10172b33] to-transparent" />
              <div className={`absolute right-4 top-4 rounded-full px-4 py-2 text-sm font-bold shadow-sm ${form.statut === 'disponible' ? 'bg-[#22c55e] text-white' : 'bg-white text-navy'}`}>
                {form.statut === 'disponible' ? 'Disponible' : 'Indisponible'}
              </div>
              <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                <p className="text-[11px] uppercase tracking-[0.2em] text-white/75">Aperçu mobile</p>
                <h4 className="mt-1 text-3xl font-extrabold leading-tight">{form.nom || 'Nom de la salle'}</h4>
                <p className="mt-2 text-sm text-white/80">
                  {[form.ville, form.quartier].filter(Boolean).join(' · ') || 'Ville · Quartier'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 p-4">
              <div className="rounded-[22px] bg-[#eef2ff] p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Capacité</p>
                <p className="mt-1 text-3xl font-extrabold text-navy">{form.capacite || '0'}</p>
                <p className="text-sm font-semibold text-navy/80">pers.</p>
              </div>
              <div className="rounded-[22px] bg-[#fff7df] p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">À partir de</p>
                <p className="mt-1 text-2xl font-extrabold text-[#f59e0b]">{formatMoney(Number(form.prix_heure || 0))}</p>
                <p className="text-sm font-semibold text-[#c98407]">/ h</p>
              </div>
              <div className="col-span-2 rounded-[22px] bg-[#f8fafc] p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Caution</p>
                <p className="mt-1 text-xl font-extrabold text-navy">{form.caution ? formatMoney(Number(form.caution)) : 'Non renseignée'}</p>
              </div>
            </div>

            <div className="px-4 pb-4">
              <div className="flex flex-wrap gap-2">
                {(selectedEquipements.length > 0 ? selectedEquipements : ['Aucun équipement']).map((equipement) => (
                  <span key={equipement} className="rounded-full border border-[#e5e7ef] bg-white px-4 py-2 text-sm font-semibold text-navy/85">
                    {equipement}
                  </span>
                ))}
              </div>
            </div>

            <div className="border-t border-[#e8ebf2] bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Description</p>
              <p className="mt-2 text-sm leading-7 text-gray-600">
                {form.description || 'La description de la salle apparaîtra ici avec ses usages recommandés et les services proposés.'}
              </p>
              {form.conditions ? (
                <div className="mt-4 rounded-[18px] bg-[#f8fafc] p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Conditions de réservation</p>
                  <p className="mt-2 text-sm leading-6 text-gray-600">{form.conditions}</p>
                </div>
              ) : null}
            </div>

            <div className="border-t border-[#e8ebf2] bg-white p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Créneaux disponibles</p>
                <p className="text-xs text-gray-400">{selectedCreneaux.length} créneau(x)</p>
              </div>
              <div className="mt-3 space-y-2">
                {(selectedCreneaux.length > 0 ? selectedCreneaux : [{ id: 'preview', label: '08h00 à 12h00', price: Number(form.prix_heure || 0) }]).map((creneau) => (
                  <div key={creneau.id} className="flex items-center justify-between rounded-[18px] border border-[#d7dceb] bg-[#f9fbff] px-4 py-3">
                    <span className="text-sm font-semibold text-navy">{creneau.label}</span>
                    <span className="text-sm font-extrabold text-[#f59e0b]">{formatMoney(creneau.price)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div className="rounded-[24px] border border-[#e8ebf2] bg-white p-4 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Informations principales</p>
              <div className="mt-4 space-y-3">
                <input value={form.nom} onChange={(event) => setForm((current) => ({ ...current, nom: event.target.value }))} placeholder="Nom de la salle" className="w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
                <div className="grid grid-cols-2 gap-3">
                  <input value={form.ville} onChange={(event) => setForm((current) => ({ ...current, ville: event.target.value }))} placeholder="Ville" className="w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
                  <input value={form.quartier} onChange={(event) => setForm((current) => ({ ...current, quartier: event.target.value }))} placeholder="Quartier" className="w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input type="number" value={form.capacite} onChange={(event) => setForm((current) => ({ ...current, capacite: event.target.value }))} placeholder="Capacité" className="w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
                  <input type="number" value={form.prix_heure} onChange={(event) => setForm((current) => ({ ...current, prix_heure: event.target.value }))} placeholder="Prix / heure" className="w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
                </div>
                <input type="number" value={form.caution} onChange={(event) => setForm((current) => ({ ...current, caution: event.target.value }))} placeholder="Caution de réservation" className="w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
                <div className="rounded-2xl border border-[#dfe4ef] bg-white p-3">
                  <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageFileChange} />
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={() => imageInputRef.current?.click()} className="rounded-xl bg-navy px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#132240] cursor-pointer whitespace-nowrap">
                      {imageFile ? 'Changer l’image' : 'Uploader une image'}
                    </button>
                    <span className="text-xs text-gray-400">
                      {imageFile ? imageFile.name : 'PNG, JPG ou JPEG'}
                    </span>
                  </div>
                  <input value={form.image} onChange={(event) => setForm((current) => ({ ...current, image: event.target.value }))} placeholder="Ou colle une URL d’image" className="mt-3 w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
                </div>
                <textarea value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} placeholder="Description de la salle" className="w-full h-28 resize-none border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
                <textarea value={form.conditions} onChange={(event) => setForm((current) => ({ ...current, conditions: event.target.value }))} placeholder="Conditions de réservation" className="w-full h-24 resize-none border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
              </div>
            </div>

            <div className="rounded-[24px] border border-[#e8ebf2] bg-white p-4 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Contact & disponibilité</p>
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input value={form.telephone} onChange={(event) => setForm((current) => ({ ...current, telephone: event.target.value }))} placeholder="Téléphone" className="w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
                  <input value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} placeholder="Email" className="w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
                </div>
                <select value={form.statut} onChange={(event) => setForm((current) => ({ ...current, statut: event.target.value as LieuForm['statut'] }))} className="w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40">
                  <option value="disponible">Disponible</option>
                  <option value="indisponible">Indisponible</option>
                </select>
              </div>
            </div>

            <div className="rounded-[24px] border border-[#e8ebf2] bg-white p-4 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Visites de la salle</p>
              <label className="mt-4 flex items-center justify-between rounded-2xl border border-[#dfe4ef] px-4 py-3">
                <span>
                  <span className="block text-sm font-bold text-navy">Autoriser les visites gratuites</span>
                  <span className="block text-xs text-gray-400">Le client pourra demander une visite avant de réserver.</span>
                </span>
                <input
                  type="checkbox"
                  checked={form.visite_autorisee}
                  onChange={(event) => setForm((current) => ({ ...current, visite_autorisee: event.target.checked }))}
                  className="h-5 w-5 accent-navy"
                />
              </label>
              <input
                value={form.heures_visite}
                onChange={(event) => setForm((current) => ({ ...current, heures_visite: event.target.value }))}
                placeholder="Heures de visite ex: 08h00, 09h00, 10h00"
                className="mt-3 w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40"
              />
            </div>

            <div className="rounded-[24px] border border-[#e8ebf2] bg-white p-4 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Équipements</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {equipmentSuggestions.map((equipement) => {
                  const active = selectedEquipements.includes(equipement);
                  return (
                    <button
                      key={equipement}
                      type="button"
                      onClick={() => toggleEquipement(equipement)}
                      className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors cursor-pointer ${
                        active
                          ? 'bg-navy text-white'
                          : 'border border-[#dfe4ef] bg-white text-navy/80 hover:bg-[#eef2ff]'
                      }`}
                    >
                      {equipement}
                    </button>
                  );
                })}
              </div>
              <input value={form.equipements} onChange={(event) => setForm((current) => ({ ...current, equipements: event.target.value }))} placeholder="Tu peux aussi saisir manuellement, séparés par des virgules" className="mt-4 w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
            </div>

            <div className="rounded-[24px] border border-[#e8ebf2] bg-white p-4 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Disponibilités calendrier</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <select value={form.mois_disponibilites} onChange={(event) => setForm((current) => ({ ...current, mois_disponibilites: event.target.value }))} className="w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40">
                  {monthLabels.map((month, index) => (
                    <option key={month} value={String(index + 1)}>{month}</option>
                  ))}
                </select>
                <input type="number" value={form.annee_disponibilites} onChange={(event) => setForm((current) => ({ ...current, annee_disponibilites: event.target.value }))} placeholder="Année" className="w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setCalendarMode('available')}
                  className={`rounded-full px-4 py-2 text-sm font-semibold cursor-pointer ${calendarMode === 'available' ? 'bg-emerald-500 text-white' : 'border border-[#dfe4ef] bg-white text-navy'}`}
                >
                  Marquer disponible
                </button>
                <button
                  type="button"
                  onClick={() => setCalendarMode('reserved')}
                  className={`rounded-full px-4 py-2 text-sm font-semibold cursor-pointer ${calendarMode === 'reserved' ? 'bg-rose-500 text-white' : 'border border-[#dfe4ef] bg-white text-navy'}`}
                >
                  Marquer réservé
                </button>
                <button
                  type="button"
                  onClick={() => setAvailableRangeFromDate(0)}
                  className="rounded-full border border-[#dfe4ef] bg-white px-4 py-2 text-sm font-semibold text-navy transition-colors hover:bg-[#eef2ff] cursor-pointer"
                >
                  Tout rendre dispo dès aujourd'hui
                </button>
                <button
                  type="button"
                  onClick={() => setAvailableRangeFromDate(1)}
                  className="rounded-full border border-[#dfe4ef] bg-white px-4 py-2 text-sm font-semibold text-navy transition-colors hover:bg-[#eef2ff] cursor-pointer"
                >
                  Tout rendre dispo dès demain
                </button>
                <button
                  type="button"
                  onClick={clearCalendarSelection}
                  className="rounded-full border border-[#dfe4ef] bg-white px-4 py-2 text-sm font-semibold text-navy transition-colors hover:bg-[#fff1f2] cursor-pointer"
                >
                  Vider le calendrier
                </button>
              </div>

              <div className="mt-4 rounded-[22px] border border-[#e1e6f2] bg-[#fbfcff] p-4">
                <div className="mb-3 text-center text-sm font-bold text-navy">
                  {monthLabels[Math.max(0, calendarMonth - 1)]} {calendarYear}
                </div>
                <div className="grid grid-cols-7 gap-2 text-center text-xs font-semibold text-gray-400">
                  {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((label, index) => (
                    <span key={`${label}-${index}`}>{label}</span>
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-7 gap-2">
                  {calendarDays.map((day, index) => {
                    if (!day) {
                      return <div key={`empty-${index}`} className="h-11" />;
                    }

                    const isAvailable = selectedDisponibilites.includes(day);
                    const isReserved = selectedReservedDates.includes(day);

                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleCalendarDay(day)}
                        className={`h-11 rounded-2xl border text-sm font-semibold transition-colors cursor-pointer ${
                          isReserved
                            ? 'border-rose-200 bg-rose-50 text-rose-500'
                            : isAvailable
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-600'
                              : 'border-[#e8ebf2] bg-white text-navy'
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <input value={form.disponibilites} onChange={(event) => setForm((current) => ({ ...current, disponibilites: event.target.value }))} placeholder="Jours disponibles ex: 1,3,4,6" className="w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
                <input value={form.dates_reservees} onChange={(event) => setForm((current) => ({ ...current, dates_reservees: event.target.value }))} placeholder="Jours réservés ex: 2,5,8" className="w-full border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
              </div>
            </div>

            <div className="rounded-[24px] border border-[#e8ebf2] bg-white p-4 shadow-[0_8px_20px_rgba(15,23,42,0.04)]">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Créneaux</p>
              <p className="mt-1 text-xs text-gray-400">Un créneau par ligne, format : `08h00 à 12h00 | 100000`</p>
              <textarea value={form.creneaux} onChange={(event) => setForm((current) => ({ ...current, creneaux: event.target.value }))} placeholder={'08h00 à 12h00 | 100000\n14h00 à 18h00 | 100000\nJournee complete (08h-18h) | 120000'} className="mt-4 w-full h-28 resize-none border border-[#dfe4ef] rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
            </div>
          </div>

          <button onClick={() => void handleSubmit()} disabled={submitting} className="w-full px-5 py-4 bg-gradient-to-r from-[#16284c] to-[#22386a] text-white rounded-2xl text-sm font-semibold hover:from-[#132240] hover:to-[#1d315d] disabled:opacity-60 cursor-pointer whitespace-nowrap shadow-[0_16px_30px_rgba(22,40,76,0.24)]">
            {submitting ? 'Enregistrement...' : editingLieuId ? 'Enregistrer les modifications' : 'Ajouter la salle'}
          </button>
        </div>

        <div className="bg-white rounded-[28px] border border-[#e6e8ef] overflow-hidden shadow-[0_18px_40px_rgba(15,23,42,0.06)]">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div>
              <h3 className="text-sm font-bold text-gray-900">Salles</h3>
              <p className="text-xs text-gray-400">{filteredLieux.length} salle(s)</p>
            </div>
            <div className="flex gap-2">
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher une salle..." className="w-64 border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40" />
              <select value={lieuStatusFilter} onChange={(event) => setLieuStatusFilter(event.target.value as typeof lieuStatusFilter)} className="border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40">
                <option value="all">Tous</option>
                <option value="disponible">Disponibles</option>
                <option value="indisponible">Indisponibles</option>
                <option value="deleted">Corbeille</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 p-5">
            {loading ? <div className="col-span-full rounded-2xl border border-dashed border-gray-200 px-5 py-8 text-sm text-gray-400">Chargement...</div> : null}
            {!loading && filteredLieux.length === 0 ? <div className="col-span-full rounded-2xl border border-dashed border-gray-200 px-5 py-8 text-sm text-gray-400">Aucune salle enregistrée.</div> : null}
            {filteredLieux.map((lieu) => (
              <div key={lieu.id} className="overflow-hidden rounded-[26px] border border-[#e8ebf2] bg-white shadow-[0_12px_24px_rgba(15,23,42,0.04)]">
                <div className="relative h-52 bg-navy">
                  <img src={lieu.image || cardFallbackImage} alt={lieu.nom} className="h-full w-full object-cover" />
                  <div className={`absolute right-4 top-4 rounded-full px-4 py-2 text-sm font-bold shadow-sm ${lieu.statut === 'disponible' ? 'bg-[#22c55e] text-white' : lieu.statut === 'deleted' ? 'bg-slate-100 text-slate-600' : 'bg-white text-navy'}`}>
                    {lieu.statut === 'disponible' ? 'Disponible' : lieu.statut === 'deleted' ? 'Corbeille' : 'Indisponible'}
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-2xl font-extrabold leading-tight text-navy">{lieu.nom}</p>
                      <p className="mt-2 text-sm text-gray-400">{lieu.ville ?? 'Ville non renseignée'}{lieu.quartier ? ` · ${lieu.quartier}` : ''}</p>
                    </div>
                    <div className="rounded-full bg-[#fff5d9] px-4 py-3 text-right">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#c58b08]">À partir de</p>
                      <p className="text-lg font-extrabold text-[#f59e0b]">{formatMoney(lieu.prix_heure)}</p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="rounded-[18px] bg-[#eef2ff] px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Capacité</p>
                      <p className="mt-1 text-lg font-extrabold text-navy">{lieu.capacite ?? 0} pers.</p>
                    </div>
                    <div className="rounded-[18px] bg-[#f8fafc] px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Contact</p>
                      <p className="mt-1 text-sm font-semibold text-navy">{lieu.telephone || lieu.email || 'Non renseigné'}</p>
                    </div>
                    <div className="col-span-2 rounded-[18px] bg-[#fff7df] px-4 py-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#c58b08]">Caution</p>
                      <p className="mt-1 text-lg font-extrabold text-[#c98407]">{lieu.caution ? formatMoney(lieu.caution) : 'Non renseignée'}</p>
                    </div>
                  </div>

                  {(lieu.equipements ?? []).length > 0 ? (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {(lieu.equipements ?? []).map((equipement) => (
                        <span key={equipement} className="rounded-full border border-[#e5e7ef] bg-white px-4 py-2 text-sm font-semibold text-navy/85">
                          {equipement}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  {lieu.creneaux?.length ? (
                    <div className="mt-4 space-y-2">
                      {lieu.creneaux.slice(0, 3).map((creneau) => (
                        <div key={creneau.id} className="flex items-center justify-between rounded-[16px] border border-[#d7dceb] bg-[#f9fbff] px-4 py-3">
                          <span className="text-sm font-semibold text-navy">{creneau.label}</span>
                          <span className="text-sm font-extrabold text-[#f59e0b]">{formatMoney(creneau.price)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div className="mt-5 flex items-center justify-end gap-2">
                    <button onClick={() => setSelectedLieu(lieu)} className="rounded-xl bg-[#eef2ff] px-4 py-2 text-sm font-semibold text-navy hover:bg-[#e2e8ff] cursor-pointer">
                      Détails
                    </button>
                    {lieu.statut === 'deleted' ? (
                      <button onClick={() => void handleRestore(lieu.id)} className="rounded-xl bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-600 hover:bg-emerald-100 cursor-pointer">
                        Restaurer
                      </button>
                    ) : (
                      <>
                        <button onClick={() => handleEdit(lieu)} className="rounded-xl bg-[#fff5d9] px-4 py-2 text-sm font-semibold text-[#c98407] hover:bg-[#ffefc0] cursor-pointer">
                          Modifier
                        </button>
                        <button onClick={() => void handleDelete(lieu.id)} className="rounded-xl bg-red-50 px-4 py-2 text-sm font-semibold text-red-500 hover:bg-red-100 cursor-pointer">
                          Supprimer
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedLieu ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-[28px] bg-white shadow-2xl">
            <div className="relative h-56 bg-navy">
              <img src={selectedLieu.image || cardFallbackImage} alt={selectedLieu.nom} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#10172bdd] via-[#10172b55] to-transparent" />
              <button
                type="button"
                onClick={() => setSelectedLieu(null)}
                className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-navy transition hover:bg-white"
                title="Fermer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <div className={`absolute left-5 top-5 rounded-full px-4 py-2 text-sm font-bold shadow-sm ${selectedLieu.statut === 'disponible' ? 'bg-[#22c55e] text-white' : 'bg-white text-navy'}`}>
                {selectedLieu.statut === 'disponible' ? 'Disponible' : 'Indisponible'}
              </div>
              <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                <h3 className="text-3xl font-extrabold leading-tight">{selectedLieu.nom}</h3>
                <p className="mt-2 text-sm text-white/75">{selectedLieu.ville ?? 'Ville non renseignée'}{selectedLieu.quartier ? ` · ${selectedLieu.quartier}` : ''}</p>
              </div>
            </div>

            <div className="max-h-[calc(92vh-14rem)] overflow-y-auto p-6">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <div className="rounded-[18px] bg-[#eef2ff] px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Capacité</p>
                  <p className="mt-1 text-lg font-extrabold text-navy">{selectedLieu.capacite ?? 0} pers.</p>
                </div>
                <div className="rounded-[18px] bg-[#fff5d9] px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#c58b08]">Prix / heure</p>
                  <p className="mt-1 text-lg font-extrabold text-[#f59e0b]">{formatMoney(selectedLieu.prix_heure)}</p>
                </div>
                <div className="rounded-[18px] bg-[#fff7df] px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#c58b08]">Caution</p>
                  <p className="mt-1 text-lg font-extrabold text-[#c98407]">{selectedLieu.caution ? formatMoney(selectedLieu.caution) : 'Non renseignée'}</p>
                </div>
                <div className="rounded-[18px] bg-[#f8fafc] px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Téléphone</p>
                  <p className="mt-1 text-sm font-semibold text-navy">{selectedLieu.telephone || 'Non renseigné'}</p>
                </div>
                <div className="rounded-[18px] bg-[#f8fafc] px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Email</p>
                  <p className="mt-1 truncate text-sm font-semibold text-navy">{selectedLieu.email || 'Non renseigné'}</p>
                </div>
              </div>

              <div className="mt-5 rounded-[20px] border border-[#e8ebf2] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Description</p>
                <p className="mt-2 text-sm leading-7 text-gray-600">{selectedLieu.description || 'Aucune description renseignée.'}</p>
                {selectedLieu.conditions ? (
                  <div className="mt-4 rounded-[18px] bg-[#f8fafc] p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Conditions de réservation</p>
                    <p className="mt-2 text-sm leading-7 text-gray-600">{selectedLieu.conditions}</p>
                  </div>
                ) : null}
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2">
                <div className="rounded-[20px] border border-[#e8ebf2] p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Disponibilités</p>
                  <p className="mt-2 text-sm font-semibold text-navy">
                    {monthLabels[Math.max(0, (selectedLieu.mois_disponibilites ?? 1) - 1)]} {selectedLieu.annee_disponibilites ?? new Date().getFullYear()}
                  </p>
                  <p className="mt-2 text-sm text-gray-500">
                    Jours disponibles : {(selectedLieu.disponibilites ?? []).length ? (selectedLieu.disponibilites ?? []).join(', ') : 'Aucun'}
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Jours réservés : {(selectedLieu.dates_reservees ?? []).length ? (selectedLieu.dates_reservees ?? []).join(', ') : 'Aucun'}
                  </p>
                </div>

                <div className="rounded-[20px] border border-[#e8ebf2] p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Équipements</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(selectedLieu.equipements ?? []).length ? (selectedLieu.equipements ?? []).map((equipement) => (
                      <span key={equipement} className="rounded-full border border-[#e5e7ef] bg-white px-3 py-1.5 text-xs font-semibold text-navy/85">
                        {equipement}
                      </span>
                    )) : <p className="text-sm text-gray-500">Aucun équipement renseigné.</p>}
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-[20px] border border-[#e8ebf2] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Créneaux</p>
                <div className="mt-3 space-y-2">
                  {(selectedLieu.creneaux ?? []).length ? (selectedLieu.creneaux ?? []).map((creneau) => (
                    <div key={creneau.id} className="flex items-center justify-between rounded-[16px] border border-[#d7dceb] bg-[#f9fbff] px-4 py-3">
                      <span className="text-sm font-semibold text-navy">{creneau.label}</span>
                      <span className="text-sm font-extrabold text-[#f59e0b]">{formatMoney(creneau.price)}</span>
                    </div>
                  )) : <p className="text-sm text-gray-500">Aucun créneau renseigné.</p>}
                </div>
              </div>

              <div className="mt-5 rounded-[20px] border border-[#e8ebf2] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">Visites</p>
                <p className="mt-2 text-sm font-semibold text-navy">
                  {selectedLieu.visite_autorisee === false ? 'Visites désactivées' : 'Visites gratuites autorisées'}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  Horaires : {(selectedLieu.heures_visite ?? []).length ? (selectedLieu.heures_visite ?? []).join(', ') : '08h00, 09h00, 10h00, 11h00, 14h00, 15h00, 16h00, 17h00'}
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button onClick={() => setSelectedLieu(null)} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer">
                  Fermer
                </button>
                <button onClick={() => handleEdit(selectedLieu)} className="rounded-xl bg-[#fff5d9] px-4 py-2 text-sm font-semibold text-[#c98407] hover:bg-[#ffefc0] cursor-pointer">
                  Modifier cette salle
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {selectedReservation ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedReservation(null)}>
          <div className="w-full max-w-2xl rounded-[28px] bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold-dark">
                  {selectedReservation.creneau_id === 'visite_gratuite' ? 'Demande de visite' : 'Demande de réservation'}
                </p>
                <h3 className="mt-1 text-xl font-extrabold text-navy">{selectedReservation.nom}</h3>
                <p className="text-sm text-gray-500">{selectedReservation.lieu_nom} · {selectedReservation.lieu_ville ?? 'Ville non renseignée'}</p>
              </div>
              <button type="button" onClick={() => setSelectedReservation(null)} className="rounded-xl p-2 text-gray-400 hover:bg-gray-100">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {[
                { label: 'Email', value: selectedReservation.email || 'Non renseigné', icon: 'ri-mail-line' },
                { label: 'Téléphone', value: selectedReservation.telephone || 'Non renseigné', icon: 'ri-phone-line' },
                { label: 'Adresse', value: selectedReservation.adresse || 'Non renseignée', icon: 'ri-map-pin-line' },
                { label: 'Date', value: selectedReservation.date_reservation, icon: 'ri-calendar-line' },
                { label: 'Créneau', value: selectedReservation.creneau_label, icon: 'ri-time-line' },
                { label: 'Montant', value: `${Number(selectedReservation.montant ?? 0).toLocaleString('fr-FR')} FCFA`, icon: 'ri-money-cny-circle-line' },
                { label: 'Statut', value: selectedReservation.statut, icon: 'ri-checkbox-circle-line' },
                { label: 'Paiement', value: selectedReservation.status_paiement || 'Non renseigné', icon: 'ri-bank-card-line' },
                { label: 'Méthode', value: selectedReservation.payment_method || 'Non renseignée', icon: 'ri-wallet-3-line' },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl border border-gray-100 bg-[#f8fafc] p-4">
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-gray-400">
                    <i className={`${item.icon} text-gold-dark`}></i>
                    {item.label}
                  </p>
                  <p className="mt-2 break-words text-sm font-semibold text-navy">{item.value}</p>
                </div>
              ))}
            </div>

            {selectedReservation.transaction_id || selectedReservation.checkout_url ? (
              <div className="mt-3 rounded-2xl border border-gray-100 bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-gray-400">Paiement</p>
                {selectedReservation.transaction_id ? <p className="mt-2 text-sm text-gray-600">Transaction : <span className="font-semibold text-navy">{selectedReservation.transaction_id}</span></p> : null}
                {selectedReservation.checkout_url ? <p className="mt-1 break-words text-sm text-gray-600">Lien : {selectedReservation.checkout_url}</p> : null}
              </div>
            ) : null}

            {selectedReservation.message ? (
              <div className="mt-3 rounded-2xl border border-gray-100 bg-[#fffaf0] p-4">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-gray-400">Message / raison</p>
                <p className="mt-2 text-sm leading-6 text-gray-700">{selectedReservation.message}</p>
              </div>
            ) : null}

            <div className="mt-5 flex justify-end">
              <button type="button" onClick={() => setSelectedReservation(null)} className="rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white">
                Fermer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex flex-col gap-3 px-5 py-4 border-b border-gray-100 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-sm font-bold text-gray-900">{reservationKind === 'visites' ? 'Demandes de visite' : 'Demandes de réservation'}</h3>
            <p className="text-xs text-gray-400">{filteredReservations.length} demande(s)</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex rounded-xl bg-[#f5f7fa] p-1">
              {[
                { key: 'reservations', label: 'Réservations' },
                { key: 'visites', label: 'Visites' },
              ].map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setReservationKind(item.key as typeof reservationKind)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${reservationKind === item.key ? 'bg-white text-navy shadow-sm' : 'text-gray-500'}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <select value={reservationStatus} onChange={(event) => setReservationStatus(event.target.value as typeof reservationStatus)} className="border border-gray-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/40">
              <option value="all">Toutes</option>
              <option value="paiement_en_attente">Paiement non validé</option>
              <option value="envoyee">Envoyées</option>
              <option value="confirmee">Confirmées</option>
              <option value="refusee">Refusées</option>
              <option value="annulee">Annulées</option>
            </select>
          </div>
        </div>

        <div className="divide-y divide-gray-50">
          {loading ? <div className="px-5 py-8 text-sm text-gray-400">Chargement...</div> : null}
          {!loading && filteredReservations.length === 0 ? <div className="px-5 py-8 text-sm text-gray-400">Aucune réservation trouvée.</div> : null}
          {paginatedReservations.map((reservation) => (
            <div key={reservation.id} className="px-5 py-4 flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900">{reservation.nom}</p>
                <p className="text-xs text-gray-400">{reservation.email}{reservation.telephone ? ` · ${reservation.telephone}` : ''}</p>
                <p className="text-xs text-gray-500 mt-1">{reservation.lieu_nom} · {reservation.lieu_ville ?? 'Ville'}</p>
                <p className="text-xs text-gray-500 mt-1">{reservation.date_reservation} · {reservation.creneau_label} · {Number(reservation.montant ?? 0).toLocaleString('fr-FR')} FCFA</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                  reservation.statut === 'confirmee'
                    ? 'bg-emerald-100 text-emerald-700'
                    : reservation.statut === 'refusee'
                      ? 'bg-red-100 text-red-700'
                      : reservation.statut === 'annulee'
                        ? 'bg-gray-100 text-gray-600'
                        : 'bg-amber-100 text-amber-700'
                }`}>
                  {reservation.statut}
                </span>
                <select
                  value={reservation.statut}
                  onChange={(event) => requestReservationStatusChange(reservation, event.target.value as ReservationLieu['statut'])}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-gold/40"
                >
                  <option value="paiement_en_attente">Paiement non validé</option>
                  <option value="envoyee">Envoyée</option>
                  <option value="confirmee">Confirmée</option>
                  <option value="refusee">Refusée</option>
                  <option value="annulee">Annulée</option>
                </select>
                <button
                  type="button"
                  onClick={() => setSelectedReservation(reservation)}
                  className="rounded-xl bg-navy px-3 py-2 text-xs font-semibold text-white hover:bg-[#132240] cursor-pointer whitespace-nowrap"
                >
                  Voir détail
                </button>
                <button
                  type="button"
                  onClick={() => setPendingReservationDelete(reservation)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-600 hover:bg-red-100 cursor-pointer"
                  title="Supprimer la demande"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
        {!loading && filteredReservations.length > RESERVATIONS_PAGE_SIZE ? (
          <div className="flex flex-col gap-3 border-t border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-gray-400">
              Page {reservationPage} sur {reservationLastPage} · {filteredReservations.length} demande(s)
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={reservationPage <= 1}
                onClick={() => setReservationPage((page) => Math.max(1, page - 1))}
                className="rounded-xl border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Précédent
              </button>
              <button
                type="button"
                disabled={reservationPage >= reservationLastPage}
                onClick={() => setReservationPage((page) => Math.min(reservationLastPage, page + 1))}
                className="rounded-xl bg-navy px-4 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Suivant
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {pendingStatusChange ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPendingStatusChange(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                <i className="ri-alert-line text-xl"></i>
              </div>
              <div>
                <h4 className="text-base font-bold text-navy">Confirmer le changement</h4>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  Voulez-vous vraiment passer la demande de <span className="font-semibold text-navy">{pendingStatusChange.reservation.nom}</span> au statut <span className="font-semibold text-navy">{reservationStatusLabels[pendingStatusChange.statut] ?? pendingStatusChange.statut}</span> ?
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingStatusChange(null)}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => void confirmReservationStatusChange()}
                className="rounded-xl bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-[#132240]"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingReservationDelete ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPendingReservationDelete(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-600">
                <i className="ri-delete-bin-line text-xl"></i>
              </div>
              <div>
                <h4 className="text-base font-bold text-navy">Supprimer la demande</h4>
                <p className="mt-2 text-sm leading-6 text-gray-600">
                  Voulez-vous vraiment supprimer la demande de <span className="font-semibold text-navy">{pendingReservationDelete.nom}</span> pour <span className="font-semibold text-navy">{pendingReservationDelete.lieu_nom}</span> ?
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingReservationDelete(null)}
                className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => void confirmReservationDelete()}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
